<template>
	<V2Layout>

		<!-- Header -->
		<div class="dn-header">
			<div>
				<h1 class="dn-header__title">{{ agencyName }}</h1>
				<p class="dn-header__sub">{{ $t(`invitation.${currentRole}`) }}</p>
			</div>
		</div>


		<!-- Quick Actions (DSP only) -->
		<div
			v-if="side === 'dsp'"
			class="dn-quick-actions"
		>
			<!-- Primary CTA: New Campaign Request -->
			<router-link
				:to="{ name: 'request-add' }"
				class="dn-qa-card dn-qa-card--primary"
			>
				<span class="dn-qa-card__icon dn-qa-card__icon--teal dn-qa-card__icon--pulse">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
						<path d="M4 13L11 8L4 3V13Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
						<circle cx="15" cy="14" r="4.5" stroke="currentColor" stroke-width="1.3" fill="rgba(42,157,143,0.15)"/>
						<path d="M15 12V16M13 14H17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
					</svg>
				</span>
				<div>
					<div class="dn-qa-card__title">New Campaign Request</div>
					<div class="dn-qa-card__sub">Ad request</div>
				</div>
			</router-link>

			<!-- Add Advertiser -->
			<router-link
				:to="{ name: 'advertiser-add' }"
				class="dn-qa-card"
			>
				<span class="dn-qa-card__icon dn-qa-card__icon--purple">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
						<circle cx="8" cy="6" r="3" stroke="currentColor" stroke-width="1.4"/>
						<path d="M2.5 17C2.5 13.7 5 11 8 11C9.3 11 10.5 11.5 11.4 12.3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
						<circle cx="15" cy="15" r="4.5" stroke="currentColor" stroke-width="1.3" fill="rgba(124,94,207,0.15)"/>
						<path d="M15 13V17M13 15H17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
					</svg>
				</span>
				<div>
					<div class="dn-qa-card__title">{{ $t('dashboard.add_advertiser') }}</div>
					<div class="dn-qa-card__sub">Create new advertiser</div>
				</div>
			</router-link>

			<!-- Add Balance -->
			<router-link
				:to="{ name: 'balance-add' }"
				class="dn-qa-card"
			>
				<span class="dn-qa-card__icon dn-qa-card__icon--blue">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
						<rect x="2" y="3" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.4"/>
						<path d="M2 6H14" stroke="currentColor" stroke-width="1.4"/>
						<circle cx="15" cy="14" r="4.5" stroke="currentColor" stroke-width="1.3" fill="rgba(37,99,235,0.15)"/>
						<path d="M15 12V16M13 14H17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
					</svg>
				</span>
				<div>
					<div class="dn-qa-card__title">{{ $t('dashboard.add_balance') }}</div>
					<div class="dn-qa-card__sub">Top up balance</div>
				</div>
			</router-link>
		</div>


		<!-- Counter Cards Grid -->
		<div class="dn-cards-grid">

			<!-- Balance Card -->
			<div class="dn-card">
				<div class="dn-card__header">
					<span class="dn-card__icon dn-card__icon--teal">
						<nice-icon2 icon="money" />
					</span>
					<span class="dn-card__label">{{ $t('balance') }}</span>
					<router-link :to="{ name: 'profile' }" class="dn-card__arrow">&rarr;</router-link>
				</div>

				<div
					v-if="totalBalance || side === 'ssp'"
					class="dn-card__value"
					v-html="formatCash(totalBalance, agencyCurrency)"
				/>
				<div v-else class="dn-card__empty">
					{{ $t('campaign.empty_balance') | capitalize }}
					<nice-hint :hint="$t('campaign.empty_balance_hint') | capitalize" />
				</div>

				<div v-if="side === 'dsp' && totalBalance" class="dn-card__balance-detail">
					<div class="dn-card__balance-bar">
						<div
							class="dn-card__balance-bar-prepaid"
							:style="{ width: prepaidPercent + '%' }"
						/>
						<div
							class="dn-card__balance-bar-credit"
							:style="{ width: creditPercent + '%' }"
						/>
					</div>
					<div class="dn-card__balance-legend">
						<span class="dn-card__balance-item">
							<span class="dn-card__balance-dot dn-card__balance-dot--prepaid" />
							<span class="dn-card__balance-label">Prepaid</span>
							<span class="dn-card__balance-val" v-html="formatCash(agencyBalanceInCurrency, agencyCurrency)" />
						</span>
						<span class="dn-card__balance-item">
							<span class="dn-card__balance-dot dn-card__balance-dot--credit" />
							<span class="dn-card__balance-label">Credit</span>
							<span class="dn-card__balance-val" v-html="formatCash(agencyCreditBalance, agencyCurrency)" />
						</span>
					</div>
				</div>

				<div class="dn-card__trends">
					<div class="dn-trend">
						<span class="dn-trend__label">{{ $t('campaign.for_week') }}</span>
						<span :class="['dn-trend__arrow', balance_counters.week_sum > balance_counters.week_sum_before ? 'dn-trend__arrow--up' : '']">
							{{ balance_counters.week_sum > balance_counters.week_sum_before ? '&#8599;' : '&#8600;' }}
						</span>
						<span class="dn-trend__val">{{ balance_counters.week_sum || 0 }}</span>
					</div>
					<div class="dn-trend">
						<span class="dn-trend__label">{{ $t('campaign.for_day') }}</span>
						<span :class="['dn-trend__arrow', balance_counters.day_sum > balance_counters.day_sum_before ? 'dn-trend__arrow--up' : '']">
							{{ balance_counters.day_sum > balance_counters.day_sum_before ? '&#8599;' : '&#8600;' }}
						</span>
						<span class="dn-trend__val">{{ balance_counters.day_sum || 0 }}</span>
					</div>
				</div>
			</div>


			<!-- Requests Card -->
			<div class="dn-card">
				<div class="dn-card__header">
					<span class="dn-card__icon dn-card__icon--purple">
						<nice-icon2 icon="bell" />
					</span>
					<span class="dn-card__label">{{ $t('request_list.requests') | capitalize }}</span>
					<router-link v-if="!isSSPViewer" :to="{ name: 'request-list' }" class="dn-card__arrow">&rarr;</router-link>
				</div>

				<div class="dn-card__value">{{ requests.total }}</div>

				<div class="dn-card__trends">
					<div class="dn-trend">
						<span class="dn-trend__label">{{ $t('campaign.for_week') }}</span>
						<span :class="['dn-trend__arrow', requests.for_week > requests.week_before ? 'dn-trend__arrow--up' : '']">
							{{ requests.for_week > requests.week_before ? '&#8599;' : '&#8600;' }}
						</span>
						<span class="dn-trend__val">{{ requests.for_week }}</span>
					</div>
					<div class="dn-trend">
						<span class="dn-trend__label">{{ $t('campaign.for_day') }}</span>
						<span :class="['dn-trend__arrow', requests.for_day > requests.day_before ? 'dn-trend__arrow--up' : '']">
							{{ requests.for_day > requests.day_before ? '&#8599;' : '&#8600;' }}
						</span>
						<span class="dn-trend__val">{{ requests.for_day }}</span>
					</div>
				</div>

				<div class="dn-card__statuses">
					<span class="dn-badge dn-badge--approval">
						<nice-icon-2 icon="warn" class="dn-badge__icon" />
						{{ requests.awaiting_approval }}
						<span class="dn-badge__text">{{ $t('campaign.status_change_1') | capitalize }}</span>
					</span>
					<span class="dn-badge dn-badge--rejected">
						<nice-icon-2 icon="error" class="dn-badge__icon" />
						{{ requests.rejected }}
						<span class="dn-badge__text">{{ $t('campaign.status_change_4') | capitalize }}</span>
					</span>
				</div>
			</div>


			<!-- Campaigns Card -->
			<div class="dn-card">
				<div class="dn-card__header">
					<span class="dn-card__icon dn-card__icon--blue">
						<nice-icon2 icon="play" />
					</span>
					<span class="dn-card__label">{{ $t('campaign.campaigns') | capitalize }}</span>
					<router-link :to="{ name: 'campaign-list' }" class="dn-card__arrow">&rarr;</router-link>
				</div>

				<div class="dn-card__value">{{ campaigns.total }}</div>

				<div class="dn-card__trends">
					<div class="dn-trend">
						<span class="dn-trend__label">{{ $t('campaign.for_week') }}</span>
						<span :class="['dn-trend__arrow', campaigns.for_week > campaigns.week_before ? 'dn-trend__arrow--up' : '']">
							{{ campaigns.for_week > campaigns.week_before ? '&#8599;' : '&#8600;' }}
						</span>
						<span class="dn-trend__val">{{ campaigns.for_week }}</span>
					</div>
					<div class="dn-trend">
						<span class="dn-trend__label">{{ $t('campaign.for_day') }}</span>
						<span :class="['dn-trend__arrow', campaigns.for_day > campaigns.day_before ? 'dn-trend__arrow--up' : '']">
							{{ campaigns.for_day > campaigns.day_before ? '&#8599;' : '&#8600;' }}
						</span>
						<span class="dn-trend__val">{{ campaigns.for_day }}</span>
					</div>
				</div>

				<div class="dn-card__statuses">
					<span class="dn-badge dn-badge--approval">
						<nice-icon-2 icon="warn" class="dn-badge__icon" />
						Awaiting {{ campaigns.awaiting_approval }}
					</span>
					<span class="dn-badge dn-badge--active">
						<nice-icon-2 icon="play" class="dn-badge__icon" />
						Playing {{ campaigns.playing }}
					</span>
					<span class="dn-badge dn-badge--paused">
						<nice-icon-2 icon="pause" class="dn-badge__icon" />
						Paused {{ campaigns.paused }}
					</span>
					<span class="dn-badge dn-badge--finished">
						<nice-icon icon="accepted" class="dn-badge__icon" />
						Finished {{ campaigns.finished }}
					</span>
					<span class="dn-badge dn-badge--rejected">
						<nice-icon-2 icon="error" class="dn-badge__icon" />
						Rejected {{ campaigns.rejected }}
					</span>
				</div>
			</div>

		</div>

		<!-- Second row: Network + Devices + Agencies (SSP only) -->
		<div v-if="side === 'ssp'" class="dn-cards-grid">

			<!-- Network Card (platforms) -->
			<div class="dn-card">
				<div class="dn-card__header">
					<span class="dn-card__icon dn-card__icon--cyan">
						<nice-icon2 icon="monitor-17" />
					</span>
					<span class="dn-card__label">Network</span>
					<router-link :to="{ name: 'network' }" class="dn-card__arrow">&rarr;</router-link>
				</div>

				<div class="dn-card__value">{{ ssp_networks.length }}</div>
				<div class="dn-card__meta">
					{{ ssp_networks.length === 1 ? 'platform' : 'platforms' }} connected
				</div>
			</div>

			<!-- Devices Card (uptime breakdown) -->
			<div class="dn-card">
				<div class="dn-card__header">
					<span class="dn-card__icon dn-card__icon--green">
						<nice-icon2 icon="monitor-17" />
					</span>
					<span class="dn-card__label">Screens</span>
					<router-link :to="{ name: 'network' }" class="dn-card__arrow">&rarr;</router-link>
				</div>

				<div class="dn-card__value">{{ deviceStats.total }}</div>

				<div class="dn-card__statuses">
					<span class="dn-badge dn-badge--online">
						{{ deviceStats.uptime_lt12h }} &lt;12h
					</span>
					<span class="dn-badge dn-badge--average">
						{{ deviceStats.uptime_gt12h }} &gt;12h
					</span>
					<span class="dn-badge dn-badge--offline">
						{{ deviceStats.uptime_gt48h }} &gt;48h
					</span>
				</div>
			</div>

			<!-- Agencies Card -->
			<div class="dn-card">
				<div class="dn-card__header">
					<span class="dn-card__icon dn-card__icon--orange">
						<nice-icon2 icon="man" />
					</span>
					<span class="dn-card__label">{{ $t('common.agencies') | capitalize }}</span>
					<router-link :to="{ name: 'agency-list' }" class="dn-card__arrow">&rarr;</router-link>
				</div>

				<div class="dn-card__value">{{ agenciesCount.total }}</div>

				<div class="dn-card__statuses">
					<span class="dn-badge dn-badge--active">
						{{ agenciesCount.active }} Active
					</span>
					<span v-if="agenciesCount.deleted > 0" class="dn-badge dn-badge--offline">
						{{ agenciesCount.deleted }} Deleted
					</span>
				</div>
			</div>
		</div>


		<!-- Analytics Section -->
		<div class="dn-analytics">
			<!-- Row 1: Title + Scale toggles -->
			<div class="dn-an-topbar">
				<h2 class="dn-section-title">Analytics</h2>
				<div class="dn-scale-toggle">
					<button
						v-for="opt in scaleOptions"
						:key="opt.value"
						:class="['dn-scale-toggle__btn', { 'dn-scale-toggle__btn--active': filterScale === opt.value }]"
						@click="filterScale = opt.value; triggerRangeUpdate()"
					>{{ opt.label }}</button>
				</div>
			</div>

			<!-- Row 2: Filters -->
			<div class="dn-an-filters">
				<div class="dn-an-filters__date">
					<nice-datetime
						v-model="filterDateRangeValue"
						range
						only-date
						:format="filterDateFormat"
						formatted="DD MMM"
						placeholder="Select dates"
						@input="triggerRangeUpdate"
					/>
				</div>
				<nice-select
					v-if="platforms.length"
					v-model="filterPlatforms"
					:options="platforms"
					multiple
					placeholder="All networks"
					class="dn-an-filters__select"
					@input="triggerRangeUpdate"
				/>
				<nice-select
					v-if="childAgencies.length"
					v-model="filterAgency"
					:options="childAgencies"
					placeholder="All agencies"
					class="dn-an-filters__select"
					@input="triggerRangeUpdate"
				/>
				<nice-select
					v-model="filterCampaign"
					:options="filterCampaignsList"
					placeholder="All campaigns"
					class="dn-an-filters__select"
					@input="triggerRangeUpdate"
				/>
			</div>

			<div class="dn-charts-grid">
			<!-- Impressions -->
			<div class="dn-chart-card">
				<div class="dn-chart-card__header">
					<span class="dn-chart-card__dot dn-chart-card__dot--purple"></span>
					<span class="dn-chart-card__label">{{ selectorLabels[0] }}</span>
					<span class="dn-chart-card__value">{{ impressions }}</span>
				</div>
				<div class="dn-chart-card__graph">
					<nice-stats-graph
						v-if="graphData.length"
						:dataset="datasetFor(0)"
						:units="selectorLabels[0]"
						:height="160"
						type="cols"
						graph-color="#7c5ecf"
						fill-color="rgba(124,94,207,0.1)"
						line-color="rgba(0,0,0,0.06)"
						:skip-bottom-legend-labels="0"
						:format-hint="formatHintImpressions"
					/>
				</div>
			</div>

			<!-- Spent / Income -->
			<div class="dn-chart-card">
				<div class="dn-chart-card__header">
					<span class="dn-chart-card__dot dn-chart-card__dot--teal"></span>
					<span class="dn-chart-card__label">{{ selectorLabels[1] }}</span>
					<span class="dn-chart-card__value" v-html="spent || 0"></span>
				</div>
				<div class="dn-chart-card__graph">
					<nice-stats-graph
						v-if="graphData.length"
						:dataset="datasetFor(1)"
						:units="selectorLabels[1]"
						:height="160"
						type="cols"
						graph-color="#2a9d8f"
						fill-color="rgba(42,157,143,0.1)"
						line-color="rgba(0,0,0,0.06)"
						:skip-bottom-legend-labels="0"
						:format-hint="formatHintSpent"
					/>
				</div>
			</div>

			<!-- Ad Plays -->
			<div class="dn-chart-card">
				<div class="dn-chart-card__header">
					<span class="dn-chart-card__dot dn-chart-card__dot--blue"></span>
					<span class="dn-chart-card__label">{{ selectorLabels[2] }}</span>
					<span class="dn-chart-card__value">{{ adPlays }}</span>
				</div>
				<div class="dn-chart-card__graph">
					<nice-stats-graph
						v-if="graphData.length"
						:dataset="datasetFor(2)"
						:units="selectorLabels[2]"
						:height="160"
						type="cols"
						graph-color="#2563eb"
						fill-color="rgba(37,99,235,0.1)"
						line-color="rgba(0,0,0,0.06)"
						:skip-bottom-legend-labels="0"
						:format-hint="formatHintAdPlays"
					/>
				</div>
			</div>
		</div>
	</div>

	</V2Layout>
</template>


<script>
import _ from 'underscore';
import { mapState, mapGetters } from 'vuex';

import { requestChildAgencies, getRequestCampaignCount } from '@/api/agency';
import apiSspDevice from '@/api/ssp/device';
import apiCampaign from '@/api/campaign';

import { formatCash, normalizeValue } from '@/utilites';
import NiceStatsGraph from '@/ui/nice-stats/nice-stats-graph';
import NiceHint from '@/ui/nice-hint';
import NiceSelect from '@/ui/nice-select';
import moment from 'moment';
import NiceDatetime from '@/ui/nice-datetime';
import V2Layout from '@/layouts/V2Layout';
import {
	getStatsGraphLabels,
	getStatsGraph,
	TIMESTAMP_KEY_FORMAT,
} from '@/ui/nice-stats/utils';
import { ROLE_SSP_ADMIN } from '@/constants/roles';


const DSP_SELECTOR_LABELS = ['impressions', 'spent', 'ad_plays'];
const SSP_SELECTOR_LABELS = ['impressions', 'income', 'ad_plays'];


export default {
	name: 'DashboardNew',

	components: {
		V2Layout,
		NiceStatsGraph,
		NiceHint,
		NiceSelect,
		NiceDatetime,
	},

	data() {
		return {
			// Stats graph
			graphData: [],
			labelData: [],
			platforms: [],
			childAgencies: [],
			campaigns: [],
			filterCampaigns: '',
			currency: '',
			selectedGraph: 0,
			impressions: 0,
			spent: 0,
			adPlays: 0,

			// Devices (uptime categories: good=<12h, average=>12h, bad=>48h)
			deviceStats: {
				total: 0,
				uptime_lt12h: 0,
				uptime_gt12h: 0,
				uptime_gt48h: 0,
			},

			// Agencies
			agenciesCount: {
				total: 0,
				active: 0,
				deleted: 0,
			},

			// Analytics filters
			filterPlatforms: [],
			filterAgency: null,
			filterCampaign: null,
			filterCampaignsList: [],
			filterDateFormat: 'DD.MM.YYYY',
			filterDateRange: {
				start: null,
				end: null,
			},
			filterScale: 'daily',
			scaleOptions: [
				{ value: 'daily', label: 'Daily' },
				{ value: 'weekly', label: 'Weekly' },
				{ value: 'monthly', label: 'Monthly' },
			],
			vertLegendCount: 5,

			// Counters (same data as dashboard-counters)
			balance_counters: {
				week_sum: 0,
				week_sum_before: 0,
				day_sum: 0,
				day_sum_before: 0,
			},
			requests: {
				total: 0,
				awaiting_approval: 0,
				pre_approved: 0,
				rejected: 0,
				for_week: 0,
				for_day: 0,
				week_before: 0,
				day_before: 0,
			},
			campaigns: {
				total: 0,
				awaiting_approval: 0,
				playing: 0,
				paused: 0,
				finished: 0,
				rejected: 0,
				for_week: 0,
				for_day: 0,
				week_before: 0,
				day_before: 0,
			},
		};
	},


	async mounted() {
		// Load counters
		await this.getCounters();

		// Populate filters and extra widgets for SSP
		if (this.currentRole === ROLE_SSP_ADMIN) {
			await this.$set(this, 'platforms', this.getPlatformChoices(this.ssp_networks));
			await this.$set(this, 'childAgencies', await this.getChildAgencyChoices());
			this.loadDeviceStats();
			this.loadAgenciesCount();
		}

		// Set default date range (last 7 days) and load initial chart data
		this.filterDateRange = {
			start: moment().subtract(7, 'days').toDate(),
			end: moment().toDate(),
		};
		this.triggerRangeUpdate();

		if (this.$route.query && this.$route.query.next) {
			this.$router.push({ path: this.$route.query.next });
		}
	},

	computed: {
		...mapState('app', {
			isSSPViewer: 'isSSPViewer',
			side: 'side',
			currentRole: 'role',
		}),

		...mapState('agency', {
			agencyName: 'name',
			agencyBalanceInCurrency: 'balance',
			agencyCreditBalance: 'credit_balance',
			agencyCurrency: 'currency',
			ssp_networks: 'ssp_networks',
		}),

		...mapState('profile', {
			profileLanguage: 'language',
			profileDateFormat: 'date_format',
			profileTimeFormat: 'time_format',
		}),

		filterDateRangeValue() {
			return {
				start: this.filterDateRange.start ? moment(this.filterDateRange.start) : null,
				end: this.filterDateRange.end ? moment(this.filterDateRange.end) : null,
			};
		},

		totalBalance() {
			return Number(this.agencyBalanceInCurrency || 0) + Number(this.agencyCreditBalance || 0);
		},

		prepaidPercent() {
			if (!this.totalBalance) return 50;
			return Math.round((Number(this.agencyBalanceInCurrency || 0) / this.totalBalance) * 100);
		},

		creditPercent() {
			if (!this.totalBalance) return 50;
			return 100 - this.prepaidPercent;
		},

		selectorLabels() {
			let selectorLabels = DSP_SELECTOR_LABELS.map(key => this.$t(key));
			if (this.side === 'ssp') {
				selectorLabels = SSP_SELECTOR_LABELS.map(key => this.$t(key));
			}
			return selectorLabels;
		},
	},


	methods: {
		formatCash,

		/**
		 * Load device stats for network widget (SSP only)
		 */
		async loadDeviceStats() {
			try {
				const result = await apiSspDevice.list({}, 1, 500);
				const devices = result.results || result || [];
				const total = result.count || devices.length;

				// Uptime categories: good = <12h, average = >12h, bad = >48h
				let uptime_lt12h = 0, uptime_gt12h = 0, uptime_gt48h = 0;
				for (let d of devices) {
					if (d.uptime === 'good') uptime_lt12h++;
					else if (d.uptime === 'average') uptime_gt12h++;
					else uptime_gt48h++;
				}

				this.deviceStats = { total, uptime_lt12h, uptime_gt12h, uptime_gt48h };
			}
			catch (err) {
				this.$log.error('Failed to load device stats', err);
			}
		},

		/**
		 * Load agencies count (SSP only)
		 */
		async loadAgenciesCount() {
			try {
				const agencies = await requestChildAgencies();
				const list = agencies.data || agencies.results || agencies || [];
				const active = list.filter(a => !a.is_deleted).length;
				const deleted = list.filter(a => a.is_deleted).length;

				this.agenciesCount = {
					total: list.length,
					active,
					deleted,
				};
			}
			catch (err) {
				this.$log.error('Failed to load agencies count', err);
			}
		},

		async getCounters() {
			try {
				let response = await getRequestCampaignCount();
				this.$set(this, 'balance_counters', Object.freeze(response.balance_counters));
				this.$set(this, 'requests', Object.freeze(response.requests));
				this.$set(this, 'campaigns', Object.freeze(response.campaigns));
			}
			catch (error) {
				this.$log.error('Failed to get counters', error);
			}
		},

		async rangeUpdated({ rangeType, dateFrom, dateTill, platforms, agency, campaign }) {
			let labels = getStatsGraphLabels(rangeType, dateFrom, dateTill);
			let graphsData = await getStatsGraph(rangeType, dateFrom, dateTill, platforms, agency, campaign);

			let graphs = [
				graphsData.impressions,
				graphsData.spentIncome,
				graphsData.adPlays,
			];

			let processedGraphs = [[], [], []];

			for (let l of labels) {
				let key = l.format(TIMESTAMP_KEY_FORMAT);
				for (let i = 0; i < graphs.length; i++) {
					let value = 0;
					if (_.has(graphs[i], key)) {
						value = graphs[i][key];
					}
					processedGraphs[i].push(value);
				}
			}

			let formatedLabels = [];
			let labelFormat = 'ddd DD MMM';
			if (rangeType === 'weekly') {
				labelFormat = '[W]W';
			} else if (rangeType === 'monthly') {
				labelFormat = 'MMM YYYY';
			}
			for (let l of labels) {
				formatedLabels.push(l.format(labelFormat));
			}

			this.$set(this, 'graphData', processedGraphs);
			this.$set(this, 'labelData', formatedLabels);

			let totalSpentInCurrency = formatCash(
				graphsData.meta.total_spent_income_in_currency,
				graphsData.meta.currency
			);

			let totalImpressions = normalizeValue(graphsData.meta.total_impressions);
			if (isNaN(totalImpressions)) {
				totalImpressions = '';
			}
			this.$set(this, 'impressions', totalImpressions);
			this.$set(this, 'spent', totalSpentInCurrency);
			this.$set(this, 'adPlays', graphsData.meta.total_ad_plays);
			this.$set(this, 'currency', graphsData.meta.currency);
		},

		getPlatformChoices(networks) {
			return networks.map((item) => ({
				value: item.id,
				label: item.name,
			}));
		},

		async getChildAgencyChoices() {
			let agencies = await requestChildAgencies();
			return agencies.map((item) => ({
				value: item.id,
				label: item.name,
			}));
		},

		async getCampaignsChoices(params) {
			let campaigns = await apiCampaign.list({
				search: params.query || '',
			});
			campaigns = campaigns.results || [];
			return _.map(campaigns, campaign => ({
				value: campaign.id,
				label: campaign.name,
			}));
		},

		formatHint(graph) {
			if (this.selectedGraph == 0 || this.selectedGraph == 2) {
				return '<span class="value">' + graph.dataset.value[graph.over] + '</span>';
			}
			return formatCash(graph.dataset.value[graph.over], this.currency);
		},

		/**
		 * Build dataset object for a specific graph index (0=impressions, 1=spent, 2=adPlays)
		 */
		datasetFor(index) {
			let d = {
				value: [],
				valueRange: [0, 100],
				columns: [],
				rows: [],
			};

			if (!this.graphData.length || !this.graphData[index]) {
				return d;
			}

			let max = Math.max(...this.graphData[index]);
			if (max === 0) max = 100;
			let step = max / (this.vertLegendCount - 1);
			let vert = Array(this.vertLegendCount).fill(null).map((_, i) => normalizeValue(i * step));

			d.value = this.graphData[index];
			d.valueRange = [0, max];
			d.columns = this.labelData;
			d.rows = vert;

			return d;
		},

		formatHintImpressions(graph) {
			return '<span class="value">' + graph.dataset.value[graph.over] + '</span>';
		},

		formatHintSpent(graph) {
			return formatCash(graph.dataset.value[graph.over], this.currency);
		},

		formatHintAdPlays(graph) {
			return '<span class="value">' + graph.dataset.value[graph.over] + '</span>';
		},

		/**
		 * Trigger analytics data reload from filters
		 */
		triggerRangeUpdate() {
			let dateFrom = this.filterDateRange && this.filterDateRange.start ? this.filterDateRange.start : null;
			let dateTill = this.filterDateRange && this.filterDateRange.end ? this.filterDateRange.end : null;

			this.rangeUpdated({
				rangeType: this.filterScale,
				dateFrom: dateFrom,
				dateTill: dateTill,
				platforms: this.filterPlatforms,
				agency: this.filterAgency,
				campaign: this.filterCampaign,
			});
		},
	},
};
</script>


<style lang="sass" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')

// ── Design tokens ─────────────────────────
$bg: #f3f4f6
$card-bg: #ffffff
$border: #e5e7eb
$text: #1a2332
$text-muted: #6b7280
$text-label: #9ca3af

$teal: #2a9d8f
$purple: #7c5ecf
$blue: #2563eb
$green: #22c55e
$orange: #f97316
$red: #ef4444

$radius-card: 16px
$radius-badge: 9999px
$shadow-card: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)
$shadow-hover: 0 8px 25px rgba(0,0,0,0.08)

$sidebar-bg: #1a2332
$sidebar-text: rgba(255,255,255,0.55)
$sidebar-text-active: #2a9d8f
$sidebar-width: 190px
$sidebar-collapsed-width: 56px

// ── Layout ───────────────────────────────
.dn-layout
	display: flex
	min-height: 100vh
	background: $bg
	font-family: 'Inter', -apple-system, sans-serif

// ── Mobile hamburger ─────────────────────
.dn-mobile-toggle
	display: none
	position: fixed
	top: 12px
	left: 12px
	z-index: 90
	width: 36px
	height: 36px
	border: none
	border-radius: 10px
	background: $card-bg
	box-shadow: $shadow-card
	cursor: pointer
	align-items: center
	justify-content: center
	color: $text

	@media (max-width: 768px)
		display: flex

// ── Mobile overlay ───────────────────────
.dn-sidebar-overlay
	display: none

	@media (max-width: 768px)
		display: block
		position: fixed
		inset: 0
		background: rgba(0,0,0,0.4)
		z-index: 99

// ── Sidebar ──────────────────────────────
.dn-sidebar
	width: $sidebar-width
	min-width: $sidebar-width
	min-height: 100vh
	background: $sidebar-bg
	display: flex
	flex-direction: column
	padding: 12px 0
	position: fixed
	top: 0
	left: 0
	z-index: 100
	transition: width 0.2s ease, min-width 0.2s ease
	overflow: hidden

	&--collapsed
		width: $sidebar-collapsed-width
		min-width: $sidebar-collapsed-width

	@media (max-width: 768px)
		transform: translateX(-100%)
		width: $sidebar-width
		min-width: $sidebar-width

		.dn-layout &
			transform: translateX(-100%)

.dn-sidebar__logo-row
	padding: 0 12px
	margin-bottom: 8px

.dn-sidebar__toggle
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	margin: 0 12px 8px auto
	border: none
	background: rgba(255,255,255,0.06)
	border-radius: 8px
	cursor: pointer
	color: rgba(255,255,255,0.4)
	transition: all 0.15s

	.dn-sidebar--collapsed &
		margin: 0 auto 8px

	&:hover
		background: rgba(255,255,255,0.12)
		color: rgba(255,255,255,0.7)

.dn-sidebar__divider
	height: 1px
	background: rgba(255,255,255,0.06)
	margin: 8px 12px

.dn-sidebar__logo
	display: flex
	align-items: center
	gap: 8px
	text-decoration: none

.dn-sidebar__logo-icon
	display: flex
	align-items: center
	justify-content: center
	width: 32px
	height: 32px
	background: $teal
	border-radius: 10px

	::v-deep .nice-icon-2
		--ni-icon-sign: #fff
		width: 16px
		height: 16px

.dn-sidebar__logo-title
	font-size: 15px
	font-weight: 700
	color: #fff
	display: block
	letter-spacing: 0.02em

.dn-sidebar__logo-sub
	font-size: 10px
	color: $sidebar-text
	display: block

.dn-sidebar__nav
	flex: 1

.dn-sidebar__group-label
	font-size: 9px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.15em
	color: rgba(255,255,255,0.25)
	padding: 16px 16px 6px
	white-space: nowrap
	overflow: hidden

	.dn-sidebar--collapsed &
		text-align: center
		padding: 16px 4px 6px
		font-size: 0
		&::first-letter
			font-size: 9px

.dn-sidebar__item
	display: flex
	align-items: center
	gap: 10px
	padding: 8px 16px
	font-size: 12px
	font-weight: 500
	color: $sidebar-text
	text-decoration: none
	transition: all 0.15s
	white-space: nowrap
	overflow: hidden

	svg
		flex-shrink: 0
		opacity: 0.6
		min-width: 16px

	&:hover
		color: rgba(255,255,255,0.85)
		background: rgba(255,255,255,0.05)

		svg
			opacity: 0.85

	&--active
		color: $sidebar-text-active
		background: rgba($teal, 0.1)
		font-weight: 600

		svg
			opacity: 1
			color: $sidebar-text-active

	.dn-sidebar--collapsed &
		justify-content: center
		padding: 10px 0

.dn-sidebar__footer
	padding: 12px 16px
	border-top: 1px solid rgba(255,255,255,0.08)

	.dn-sidebar--collapsed &
		padding: 12px 0
		display: flex
		justify-content: center

	::v-deep .account-menu__trigger
		--trigger_bg_color: #{$teal}
		--trigger_bg_color_hover: #{darken($teal, 8%)}

// ── Header ───────────────────────────────
.dn-header
	margin-bottom: 20px

.dn-header__title
	font-size: 22px
	font-weight: 700
	margin: 0

.dn-header__sub
	font-size: 12px
	color: $text-muted
	margin-top: 2px

// ── Quick Actions ────────────────────────
.dn-quick-actions
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 12px
	margin-bottom: 20px

	@media (max-width: 768px)
		grid-template-columns: 1fr

.dn-qa-card
	display: flex
	align-items: center
	gap: 12px
	padding: 14px 16px
	background: $card-bg
	border: 1px solid $border
	border-radius: $radius-card
	text-decoration: none
	color: $text
	box-shadow: $shadow-card
	transition: all 0.2s ease

	&:hover
		box-shadow: $shadow-hover
		transform: translateY(-1px)

.dn-qa-card--primary
	border-color: rgba($teal, 0.3)
	background: linear-gradient(135deg, rgba($teal, 0.04), rgba($teal, 0.01))
	animation: dn-pulse 3s ease-in-out infinite

	&:hover
		border-color: rgba($teal, 0.5)
		animation: none

@keyframes dn-pulse
	0%, 100%
		box-shadow: 0 0 0 0 rgba($teal, 0)
	50%
		box-shadow: 0 0 0 4px rgba($teal, 0.1)

.dn-qa-card__icon
	display: flex
	align-items: center
	justify-content: center
	width: 36px
	height: 36px
	border-radius: 10px
	flex-shrink: 0

	svg
		width: 20px
		height: 20px

	::v-deep .nice-icon-2
		width: 18px
		height: 18px

	&--purple
		background: rgba($purple, 0.12)
		color: $purple
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$purple}

	&--teal
		background: rgba($teal, 0.12)
		color: $teal
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$teal}

	&--blue
		background: rgba($blue, 0.12)
		color: $blue
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$blue}

.dn-qa-card__title
	font-size: 13px
	font-weight: 600

.dn-qa-card__sub
	font-size: 11px
	color: $text-muted

// ── Cards Grid ───────────────────────────
.dn-cards-grid
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 12px
	margin-bottom: 20px

	@media (max-width: 768px)
		grid-template-columns: 1fr

.dn-cards-grid--two
	grid-template-columns: repeat(2, 1fr)

// ── Counter Card ─────────────────────────
.dn-card
	background: $card-bg
	border: 1px solid $border
	border-radius: $radius-card
	padding: 18px 20px
	box-shadow: $shadow-card
	display: flex
	flex-direction: column

.dn-card__header
	display: flex
	align-items: center
	gap: 8px
	margin-bottom: 14px

.dn-card__icon
	display: flex
	align-items: center
	justify-content: center
	width: 28px
	height: 28px
	border-radius: 8px
	flex-shrink: 0

	::v-deep .nice-icon-2
		width: 14px
		height: 14px

	&--teal
		background: rgba($teal, 0.12)
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$teal}

	&--purple
		background: rgba($purple, 0.12)
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$purple}

	&--blue
		background: rgba($blue, 0.12)
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$blue}

	&--cyan
		background: rgba(#0891b2, 0.12)
		::v-deep .nice-icon-2
			--ni-icon-sign: #0891b2

	&--green
		background: rgba($green, 0.12)
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$green}

	&--orange
		background: rgba($orange, 0.12)
		::v-deep .nice-icon-2
			--ni-icon-sign: #{$orange}

.dn-card__label
	font-size: 13px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $text-muted

.dn-card__arrow
	margin-left: auto
	font-size: 16px
	color: $text-label
	text-decoration: none
	transition: color 0.15s

	&:hover
		color: $teal

.dn-card__value
	font-size: 28px
	font-weight: 800
	letter-spacing: -0.02em
	margin-bottom: 12px
	font-variant-numeric: tabular-nums

	::v-deep .nobr
		white-space: nowrap

.dn-card__empty
	font-size: 14px
	color: $text-muted
	margin-bottom: 12px

.dn-card__balance-detail
	margin-bottom: 14px

// Stacked bar
.dn-card__balance-bar
	display: flex
	height: 6px
	border-radius: 3px
	overflow: hidden
	background: #f3f4f6
	margin-bottom: 10px

.dn-card__balance-bar-prepaid
	background: $teal
	border-radius: 3px 0 0 3px
	min-width: 4px
	transition: width 0.3s ease

.dn-card__balance-bar-credit
	background: $purple
	border-radius: 0 3px 3px 0
	min-width: 4px
	transition: width 0.3s ease

// Legend
.dn-card__balance-legend
	display: flex
	gap: 20px

.dn-card__balance-item
	display: flex
	align-items: center
	gap: 5px

.dn-card__balance-dot
	width: 8px
	height: 8px
	border-radius: 50%
	flex-shrink: 0

	&--prepaid
		background: $teal

	&--credit
		background: $purple

.dn-card__balance-label
	font-size: 11px
	color: $text-label
	font-weight: 500

.dn-card__balance-val
	font-size: 13px
	font-weight: 600
	color: $text

.dn-card__meta
	font-size: 12px
	color: $text-muted
	margin-bottom: 12px

// ── Trends ───────────────────────────────
.dn-card__trends
	display: flex
	gap: 16px
	margin-bottom: 12px

.dn-trend
	display: flex
	align-items: center
	gap: 4px

.dn-trend__label
	font-size: 11px
	color: $text-label
	background: #f3f4f6
	padding: 2px 8px
	border-radius: 6px

.dn-trend__arrow
	font-size: 14px
	color: $text-muted

	&--up
		color: $green

.dn-trend__val
	font-size: 13px
	font-weight: 600
	font-variant-numeric: tabular-nums

// ── Status Badges ────────────────────────
.dn-card__statuses
	display: flex
	flex-wrap: wrap
	gap: 6px
	margin-top: auto
	padding-top: 12px
	border-top: 1px solid $border

.dn-badge
	display: inline-flex
	align-items: center
	gap: 4px
	padding: 4px 10px
	border-radius: $radius-badge
	font-size: 11px
	font-weight: 600
	font-variant-numeric: tabular-nums

	.dn-badge__icon
		width: 14px
		height: 14px

	.dn-badge__text
		font-weight: 500

	&--approval
		background: rgba(#eab308, 0.1)
		color: #a16207

	&--active
		background: rgba($teal, 0.1)
		color: darken($teal, 10%)

	&--paused
		background: rgba($purple, 0.1)
		color: $purple

	&--finished
		background: rgba($blue, 0.1)
		color: $blue

	&--rejected
		background: rgba($red, 0.1)
		color: $red

	&--online
		background: rgba($green, 0.1)
		color: darken($green, 10%)

	&--average
		background: rgba($purple, 0.1)
		color: $purple

	&--offline
		background: rgba(#6b7280, 0.1)
		color: #6b7280

// ── Analytics Card ───────────────────────
// ── Analytics Section ─────────────────────
.dn-analytics
	background: $card-bg
	border: 1px solid $border
	border-radius: 20px
	padding: 24px 28px
	box-shadow: $shadow-card
	margin-bottom: 20px

.dn-an-topbar
	display: flex
	align-items: center
	justify-content: space-between
	margin-bottom: 16px

.dn-section-title
	font-size: 13px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.06em
	color: $text-muted
	margin: 0

// Scale toggle buttons
.dn-scale-toggle
	display: flex
	background: #f3f4f6
	border-radius: 10px
	padding: 3px

.dn-scale-toggle__btn
	border: none
	background: transparent
	padding: 6px 16px
	font-size: 12px
	font-weight: 500
	font-family: 'Inter', sans-serif
	color: $text-muted
	border-radius: 8px
	cursor: pointer
	transition: all 0.15s

	&:hover
		color: $text

	&--active
		background: $card-bg
		color: $text
		font-weight: 600
		box-shadow: 0 1px 3px rgba(0,0,0,0.08)

// Filters row
.dn-an-filters
	display: flex
	align-items: center
	gap: 10px
	margin-bottom: 20px
	flex-wrap: wrap

.dn-an-filters__date
	flex-shrink: 0

	::v-deep
		.field-label
			display: none !important

		.datepicker
			right: auto

		.date-time-picker
			font-family: 'Inter', sans-serif

		input
			font-size: 12px !important
			font-family: 'Inter', sans-serif
			padding: 7px 10px !important
			border-radius: 10px !important
			border: 1px solid $border !important
			background: #f8f9fb !important
			color: $text !important
			width: 130px !important

			&:focus
				border-color: $teal !important

.dn-an-filters__select
	flex: 1
	min-width: 120px
	max-width: 180px

	::v-deep
		.ni_select--label
			display: none !important

		.ni_select--select-row
			background: #f8f9fb
			border: 1px solid $border
			border-radius: 10px
			padding: 0 10px
			height: 34px
			transition: border-color 0.15s

			&:hover
				border-color: darken($border, 10%)

		.ni_select--input-wrapper
			height: 34px

		.ni_select--caption
			font-size: 12px
			font-family: 'Inter', sans-serif
			color: $text-muted
			line-height: 34px

		.ni_select--input
			font-size: 12px
			font-family: 'Inter', sans-serif

		.ni_select--button_dd
			opacity: 0.4

		.ni_select--options
			border-radius: 12px
			box-shadow: 0 8px 30px rgba(0,0,0,0.12)
			border: 1px solid $border
			margin-top: 4px

// ── Charts Grid ──────────────────────────
.dn-charts-grid
	display: grid
	grid-template-columns: repeat(3, 1fr)
	gap: 14px

	@media (max-width: 768px)
		grid-template-columns: 1fr

.dn-chart-card
	background: $card-bg
	border: 1px solid $border
	border-radius: 20px
	padding: 0
	box-shadow: $shadow-card
	overflow: hidden
	position: relative

	// colored top accent bar
	&::before
		content: ''
		display: block
		height: 3px
		width: 100%

	&:nth-child(1)::before
		background: linear-gradient(90deg, $purple, lighten($purple, 20%))
	&:nth-child(2)::before
		background: linear-gradient(90deg, $teal, lighten($teal, 20%))
	&:nth-child(3)::before
		background: linear-gradient(90deg, $blue, lighten($blue, 20%))

.dn-chart-card__header
	display: flex
	align-items: center
	gap: 10px
	padding: 16px 20px 0

.dn-chart-card__dot
	width: 10px
	height: 10px
	border-radius: 50%
	flex-shrink: 0
	box-shadow: 0 0 0 3px rgba(0,0,0,0.06)

	&--purple
		background: $purple

	&--teal
		background: $teal

	&--blue
		background: $blue

.dn-chart-card__label
	font-size: 10px
	font-weight: 700
	text-transform: uppercase
	letter-spacing: 0.12em
	color: $text-muted

.dn-chart-card__value
	margin-left: auto
	font-size: 22px
	font-weight: 800
	font-variant-numeric: tabular-nums
	letter-spacing: -0.02em

.dn-chart-card__graph
	padding: 8px 4px 0
	overflow: hidden

	::v-deep .stat-graph
		background: transparent
		margin: 0
		padding: 0

		.label
			display: none

		.legend-y
			width: 32px

			span
				font-size: 9px
				color: $text-label
				font-family: 'Inter', sans-serif

		.graph
			background: transparent

		.bottomLegend
			span
				font-size: 9px
				color: $text-label
				font-family: 'Inter', sans-serif

		.tooltip
			border-radius: 12px
			box-shadow: 0 8px 30px rgba(0,0,0,0.12)
			font-family: 'Inter', sans-serif
			font-size: 12px
			border: 1px solid $border
			padding: 10px 14px

		path
			transition: opacity 0.2s

		circle
			transition: all 0.15s
</style>
